import React, { Component } from 'react';
import _ from 'lodash';
import moment from 'moment';

import SearchBar from './header/SearchBar';
import Filter from './filter/Filter';
import MapComponent from './map/MapComponent';
import ObservationComponent from './observation/ObservationComponent';

import './map/Map.scss';
import HistogramPanel from './histogram/HistogramPanel';
import { isMobileViewport, setHeaderMenuOpen } from '../../utils/mobileUtil';

class Home extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filterCollapsed: false,
      filterExpanded: false,
      filterPanelWidthPx: null,
      resultsCollapsed: false,
      resultsPanelWidthPx: null,
      isMobile: isMobileViewport(),
      mobileView: 'map',
      selectedAreas: [],
      selectedDataSources: [],
      selectedAgents: [],
      start: null,
      end: null,
      filterTreeFetched: false,
      ontologyLoading: false,
      dataLoading: {
        inFlight: false,
        completed: 0,
        total: 0,
        currentIndex: 0,
        current: '',
        parallelSteps: [],
        verb: 'Loading'
      },
      transientLoading: {
        inFlight: false,
        completed: 0,
        total: 0,
        currentIndex: 0,
        current: '',
        parallelSteps: [],
        verb: 'Loading'
      },
      resetVersion: 0,
      expandPeriodVersion: 0,
      histogramHeightPx: 28 // collapsed handle only
    };
    this._handleHistogramHeight = this._handleHistogramHeight.bind(this);
    this._lastHistogramHeight = 28;

    this.fetchAllDataInFlight = false;
    this.fetchAllDataPending = false;
    this.pendingFetchOptions = null;
    this.pendingDebouncedFetchOptions = null;
    this.pendingForceFetchOptions = null;
    this.forceFetchTimer = null;
    this.waitForBoundsRetryTimer = null;
    this.lastAlertRefreshAt = 0;
    this.hasCompletedInitialDataLoad = false;
    this.lastCompletedQueryFingerprint = null;

    this.flushFetchAllData = _.debounce(() => {
      const options = this.pendingDebouncedFetchOptions || {};
      this.pendingDebouncedFetchOptions = null;
      this.performFetchAllData(options);
    }, 300);
    this.fetchAllData = (options = {}) => {
      const requestedScope = options.scope === 'targeted' ? 'targeted' : 'full';
      if (options.force) {
        const previousForceOptions = this.pendingForceFetchOptions || {};
        this.pendingForceFetchOptions = {
          refreshAlerts:
            Boolean(previousForceOptions.refreshAlerts) ||
            Boolean(options.refreshAlerts),
          scope:
            previousForceOptions.scope === 'full' || requestedScope === 'full'
              ? 'full'
              : 'targeted',
          // If any queued force call is non-silent, run full update.
          silent:
            (previousForceOptions.silent ?? true) && (options.silent ?? false)
        };
        if (this.forceFetchTimer) {
          clearTimeout(this.forceFetchTimer);
        }
        this.forceFetchTimer = setTimeout(() => {
          const forcedOptions = this.pendingForceFetchOptions || {};
          this.pendingForceFetchOptions = null;
          this.forceFetchTimer = null;
          this.pendingDebouncedFetchOptions = null;
          if (this.flushFetchAllData && this.flushFetchAllData.cancel) {
            this.flushFetchAllData.cancel();
          }
          this.performFetchAllData(forcedOptions);
        }, 350);
        return;
      }
      const previousOptions = this.pendingDebouncedFetchOptions || {};
      this.pendingDebouncedFetchOptions = {
        refreshAlerts:
          Boolean(previousOptions.refreshAlerts) ||
          Boolean(options.refreshAlerts),
        scope:
          previousOptions.scope === 'full' || requestedScope === 'full'
            ? 'full'
            : 'targeted',
        // If any queued call is non-silent, run a full update.
        silent: (previousOptions.silent ?? true) && (options.silent ?? false)
      };
      this.flushFetchAllData();
    };
    this.fetchAllData.cancel = () => {
      this.pendingDebouncedFetchOptions = null;
      if (this.flushFetchAllData && this.flushFetchAllData.cancel) {
        this.flushFetchAllData.cancel();
      }
    };
    this.getLKPData = _.debounce(this.getLKPData, 500);
  }

  async componentDidMount() {
    // Always start from bounds + time baseline; avoid hidden stale facet/query filters.
    this.props.setSelectedConcepts([]);
    this.props.setSelectedDataSources([]);
    this.props.setSelectedAgents([]);
    this.props.setSearchQuery('');

    this.props.getAggregates();
    this.loadFilterTree();
    this.props.getLabels();
    this.fetchAllData({ refreshAlerts: true });
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    if (this.fetchAllData && this.fetchAllData.cancel) {
      this.fetchAllData.cancel();
    }
    if (this.forceFetchTimer) {
      clearTimeout(this.forceFetchTimer);
      this.forceFetchTimer = null;
    }
    if (this.waitForBoundsRetryTimer) {
      clearTimeout(this.waitForBoundsRetryTimer);
      this.waitForBoundsRetryTimer = null;
    }
    this.pendingForceFetchOptions = null;
    window.removeEventListener('resize', this.handleResize);
  }

  // Mobile: the Results view is empty and nothing is loading, so there is
  // nothing to show there.
  isMobileResultsEmpty = (props, state) =>
    Boolean(
      props.mapBoundsReady &&
        state.isMobile &&
        state.mobileView === 'results' &&
        !state.dataLoading.inFlight &&
        !state.transientLoading.inFlight &&
        !props.timelineTotal
    );

  componentDidUpdate(prevProps, prevState) {
    // Send the user back to Filters, with the Period group expanded, when the
    // Results view turns out empty. Edge-triggered, so it fires once.
    if (
      this.isMobileResultsEmpty(this.props, this.state) &&
      !this.isMobileResultsEmpty(prevProps, prevState)
    ) {
      this.setState(state => ({
        mobileView: 'filters',
        expandPeriodVersion: state.expandPeriodVersion + 1
      }));
    }

    if (!prevProps.mapBoundsReady && this.props.mapBoundsReady) {
      this.fetchAllData({ refreshAlerts: true });
    }

    const timePeriodChanged =
      prevProps.queryStart !== this.props.queryStart ||
      prevProps.queryEnd !== this.props.queryEnd;
    if (timePeriodChanged && this.props.mapBoundsReady) {
      // Prevent stale values from previous time windows from lingering in UI.
      this.clearDependentData({
        clearConcepts: false,
        clearTimeline: false,
        clearLayerFeatures: false
      });
      if (this.fetchAllData && this.fetchAllData.cancel) {
        this.fetchAllData.cancel();
      }
      this.fetchAllData({ refreshAlerts: true });
    }
  }

  handleResize = () => {
    const isMobile = isMobileViewport();
    const nextState = {};
    if (isMobile !== this.state.isMobile) {
      nextState.isMobile = isMobile;
      nextState.mobileView = 'map';
    }
    if (isMobile && this.state.filterPanelWidthPx !== null) {
      nextState.filterPanelWidthPx = null;
    }
    if (isMobile && this.state.resultsPanelWidthPx !== null) {
      nextState.resultsPanelWidthPx = null;
    }
    if (Object.keys(nextState).length > 0) {
      this.setState(nextState);
    }
  };

  getQueryFingerprint = () =>
    JSON.stringify({
      north: this.props.queryNorth,
      east: this.props.queryEast,
      south: this.props.querySouth,
      west: this.props.queryWest,
      start: this.props.queryStart,
      end: this.props.queryEnd,
      searchQuery: this.props.querySearch || '',
      concepts: this.props.queryConcepts || [],
      dataSources: this.props.queryDataSources || [],
      agents: this.props.queryAgents || [],
      areas: this.props.queryAreas || [],
      mode: this.props.queryMode
    });

  getFilterResizeBounds = () => {
    const viewportWidth = Math.max(window.innerWidth || 0, 1024);
    const minWidthPx = 220;
    const maxWidthPx = Math.max(
      minWidthPx + 80,
      Math.min(520, Math.round(viewportWidth * 0.32))
    );
    return { minWidthPx, maxWidthPx };
  };

  setFilterPanelWidth = widthPx => {
    const { minWidthPx, maxWidthPx } = this.getFilterResizeBounds();
    const nextWidth = Math.max(
      minWidthPx,
      Math.min(maxWidthPx, Math.round(Number(widthPx) || minWidthPx))
    );
    this.setState({
      filterCollapsed: false,
      filterExpanded: true,
      filterPanelWidthPx: nextWidth
    });
  };

  getResultsResizeBounds = () => {
    const viewportWidth = Math.max(window.innerWidth || 0, 1024);
    const minWidthPx = 280;
    const maxWidthPx = Math.max(
      minWidthPx + 80,
      Math.min(640, Math.round(viewportWidth * 0.42))
    );
    return { minWidthPx, maxWidthPx };
  };

  setResultsPanelWidth = widthPx => {
    const { minWidthPx, maxWidthPx } = this.getResultsResizeBounds();
    const nextWidth = Math.max(
      minWidthPx,
      Math.min(maxWidthPx, Math.round(Number(widthPx) || minWidthPx))
    );
    this.setState({
      resultsCollapsed: false,
      resultsPanelWidthPx: nextWidth
    });
  };

  toggleResultsPanel = () => {
    this.setState(prevState => ({
      resultsCollapsed: !prevState.resultsCollapsed
    }));
  };

  getTimeline = async () => {
    this.props.setScrollToTop(true);
    const request = this.props.getTimeline(false);
    this.props.setScrollToTop(false);
    return request;
  };

  loadFilterTree = async (options = {}) => {
    this.setState(prevState => ({
      ontologyLoading: true,
      dataLoading: prevState.dataLoading.inFlight
        ? prevState.dataLoading
        : {
            inFlight: true,
            completed: 0,
            total: 0,
            currentIndex: 0,
            current: 'Sensing Clues data model',
            verb: this.hasCompletedInitialDataLoad ? 'Updating' : 'Loading'
          }
    }));

    try {
      const response = await this.awaitWithTimeout(
        this.props.getFilterTreeList(undefined, options),
        45000,
        'ontology hierarchy'
      );
      if (response && response.timedOut) {
        await this.props.getFilterTreeList(undefined, {
          ...options,
          forceRefresh: false,
          offlineOnly: true
        });
      }
    } finally {
      this.setState(prevState => ({
        ontologyLoading: false,
        filterTreeFetched: true,
        dataLoading:
          this.fetchAllDataInFlight || this.fetchAllDataPending
            ? prevState.dataLoading
            : {
                inFlight: false,
                completed: 0,
                total: 0,
                currentIndex: 0,
                current: '',
                verb: this.hasCompletedInitialDataLoad ? 'Updating' : 'Loading'
              }
      }));
    }
  };

  getFacetValuesCount = (response, key) => {
    const facetValues =
      response &&
      response.facets &&
      response.facets[key] &&
      response.facets[key].facetValues
        ? response.facets[key].facetValues
        : [];
    return facetValues.length;
  };

  getFacetTotalCount = (response, key) => {
    const facetValues =
      response &&
      response.facets &&
      response.facets[key] &&
      response.facets[key].facetValues
        ? response.facets[key].facetValues
        : [];
    if (!facetValues.length) return 0;
    return facetValues.reduce(
      (sum, item) => sum + (Number(item.count) || 0),
      0
    );
  };

  clearDependentData = async (options = {}) => {
    const {
      clearConcepts = true,
      clearTimeline = true,
      clearLayerFeatures = true
    } = options;
    const operations = [
      Promise.resolve(this.props.clearTimelineFacets()),
      Promise.resolve(this.props.clearEntireTrack()),
      Promise.resolve(this.props.setSelectedDataSources([])),
      Promise.resolve(this.props.setSelectedAgents([]))
    ];
    if (clearTimeline) {
      operations.push(Promise.resolve(this.props.clearTimelineResults()));
    }
    if (clearLayerFeatures) {
      operations.push(Promise.resolve(this.props.clearCommonLayerFeatures()));
    }
    if (clearConcepts) {
      operations.push(Promise.resolve(this.props.clearConceptsCount()));
    }
    await Promise.allSettled(operations);
  };

  awaitWithTimeout = async (promise, timeoutMs, label) => {
    let timeoutId = null;
    const timeoutPromise = new Promise(resolve => {
      timeoutId = setTimeout(() => resolve({ timedOut: true }), timeoutMs);
    });

    try {
      const result = await Promise.race([
        Promise.resolve(promise),
        timeoutPromise
      ]);
      if (result && result.timedOut) {
        // eslint-disable-next-line no-console
        console.warn(`fetchAllData step timed out: ${label}`);
      }
      return result;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  performFetchAllData = async (options = {}) => {
    const { refreshAlerts = false, silent = false } = options;
    const scope = options.scope === 'targeted' ? 'targeted' : 'full';
    let startedPipeline = false;
    const verb = this.hasCompletedInitialDataLoad ? 'Updating' : 'Loading';
    const queryFingerprint = this.getQueryFingerprint();

    if (this.fetchAllDataInFlight) {
      // Background refreshes should not restart the visible loading pipeline.
      if (silent) {
        return;
      }

      if (scope === 'targeted') {
        const hasFreshFullSyncForFingerprint =
          this.lastCompletedQueryFingerprint === queryFingerprint;
        if (hasFreshFullSyncForFingerprint && !refreshAlerts) {
          await Promise.resolve(this.getTimeline());
          return;
        }

        if (!silent) {
          this.setState({
            dataLoading: {
              inFlight: true,
              completed: 0,
              total: 0,
              currentIndex: 0,
              current: 'results',
              parallelSteps: [
                'results',
                'concept counts',
                'data sources',
                'agent list',
                'map markers'
              ],
              verb
            }
          });
        }

        await Promise.allSettled([
          Promise.resolve(this.getTimeline()),
          Promise.resolve(this.props.getAggregates()),
          Promise.resolve(this.props.getConceptsCount()),
          Promise.resolve(this.props.getDataSources()),
          Promise.resolve(this.props.getAgents()),
          Promise.resolve(
            this.props.getAllLayerFeatures({
              onlyCommon: true,
              commonLayerNames: ['Observations', 'Tracks', 'Agents']
            })
          )
        ]);

        const now = Date.now();
        const shouldRefreshAlerts =
          refreshAlerts || now - this.lastAlertRefreshAt >= 60000;
        if (shouldRefreshAlerts) {
          await Promise.resolve(this.props.syncAlertsFromTimelineResults());
          this.lastAlertRefreshAt = now;
        }
        this.lastCompletedQueryFingerprint = queryFingerprint;
        return;
      }
      this.fetchAllDataPending = true;
      const previousOptions = this.pendingFetchOptions || {};
      this.pendingFetchOptions = {
        refreshAlerts: previousOptions.refreshAlerts || refreshAlerts,
        // If any queued request is non-silent, run full pipeline next.
        silent: (previousOptions.silent ?? true) && silent
      };
      return;
    }

    this.fetchAllDataInFlight = true;
    try {
      if (!this.props.mapBoundsReady) {
        if (!silent) {
          this.setState({
            dataLoading: {
              inFlight: true,
              completed: 0,
              total: 5,
              currentIndex: 3,
              current: 'map bounds',
              parallelSteps: [],
              verb
            }
          });
        }
        if (!this.waitForBoundsRetryTimer) {
          const retryOptions = { refreshAlerts, silent, force: true };
          this.waitForBoundsRetryTimer = setTimeout(() => {
            this.waitForBoundsRetryTimer = null;
            this.fetchAllData(retryOptions);
          }, 350);
        }
        return;
      }
      if (this.waitForBoundsRetryTimer) {
        clearTimeout(this.waitForBoundsRetryTimer);
        this.waitForBoundsRetryTimer = null;
      }
      startedPipeline = true;

      if (silent) {
        const silentRefreshResult = await this.awaitWithTimeout(
          this.props.getAllLayerFeatures({
            onlyCommon: true
          }),
          120000,
          'map markers (silent)'
        );
        if (
          this.props.setLastKnownUpdate &&
          !(silentRefreshResult && silentRefreshResult.timedOut)
        ) {
          this.props.setLastKnownUpdate();
        }
        return;
      }

      const total = 7;
      let completed = 0;
      const stepDurationsMs = {};
      const trackedStepOrder = [
        'data sources',
        'concept counts',
        'results',
        'markers fetch',
        'markers render',
        'agent list',
        'agent markers'
      ];
      const formatDuration = durationMs => `${(durationMs / 1000).toFixed(2)}s`;
      const waitForNextPaint = () =>
        new Promise(resolve => {
          if (
            typeof window !== 'undefined' &&
            typeof window.requestAnimationFrame === 'function'
          ) {
            window.requestAnimationFrame(() => resolve());
            return;
          }
          setTimeout(resolve, 16);
        });
      const finishStepTiming = (stepName, startedAt) => {
        const durationMs = Math.max(0, performance.now() - startedAt);
        stepDurationsMs[stepName] = durationMs;
        // eslint-disable-next-line no-console
        console.info(
          `[fetchAllData] ${stepName}: ${formatDuration(durationMs)}`
        );
      };
      const setLoadingStep = (currentIndex, current, parallelSteps = []) => {
        if (silent) return;
        this.setState({
          dataLoading: {
            inFlight: true,
            completed,
            total,
            currentIndex,
            current,
            parallelSteps,
            verb
          }
        });
      };

      setLoadingStep(1, 'map bounds');

      // 1) map bounds
      try {
        await this.awaitWithTimeout(
          Promise.resolve(this.props.mapBoundsReady),
          5000,
          'map bounds'
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('fetchAllData step failed: bounds', error);
      }
      completed += 1;

      // Start timeline fetch early so result list updates as soon as possible,
      // while subsequent steps continue.
      const resultsStartedAt = performance.now();
      const resultsPromise = this.awaitWithTimeout(
        Promise.resolve(this.getTimeline()),
        60000,
        'results'
      );

      // Kick off aggregates fetch in parallel — feeds the histogram
      this.awaitWithTimeout(
        Promise.resolve(this.props.getAggregates()),
        30000,
        'aggregates'
      ).catch(() => {});

      // Start concept counts early in parallel with data sources so its
      // latency is hidden behind the data sources request.
      const conceptCountsStartedAt = performance.now();
      const conceptCountsPromise = this.awaitWithTimeout(
        Promise.resolve(this.props.getConceptsCount()),
        60000,
        'concept counts'
      );

      // Start marker fetch early in parallel with data sources and concept
      // counts, so markers are ready to render as soon as those steps complete.
      const markersFetchStartedAt = performance.now();
      const markersPromise = this.awaitWithTimeout(
        this.props.getAllLayerFeatures({
          onlyCommon: true,
          commonLayerNames: ['Observations', 'Tracks']
        }),
        120000,
        'map markers'
      );

      // 2) data sources
      let sourcesResponse = null;
      const dataSourcesStartedAt = performance.now();
      setLoadingStep(2, 'data sources');
      try {
        sourcesResponse = await this.awaitWithTimeout(
          Promise.resolve(this.props.getDataSources()),
          25000,
          'data sources'
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('fetchAllData step failed: sources', error);
      } finally {
        finishStepTiming('data sources', dataSourcesStartedAt);
      }
      completed += 1;

      const sourcesFetchOk =
        sourcesResponse &&
        !sourcesResponse.timedOut &&
        !sourcesResponse.isError;
      const sourceTotalCount = sourcesFetchOk
        ? this.getFacetTotalCount(sourcesResponse, 'dataSources')
        : null;
      if (sourceTotalCount === 0) {
        await this.clearDependentData();
        return;
      }

      // 3) concept counts + (overlap) 4) results
      setLoadingStep(3, 'concept counts', ['concept counts', 'results']);
      try {
        await conceptCountsPromise;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('fetchAllData step failed: concepts', error);
      } finally {
        finishStepTiming('concept counts', conceptCountsStartedAt);
      }
      completed += 1;

      setLoadingStep(4, 'results');
      try {
        await resultsPromise;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('fetchAllData step failed: results', error);
      } finally {
        finishStepTiming('results', resultsStartedAt);
      }
      completed += 1;

      const now = Date.now();
      const shouldRefreshAlerts =
        refreshAlerts || now - this.lastAlertRefreshAt >= 60000;

      if (shouldRefreshAlerts) {
        if (!silent) {
          this.setState({
            dataLoading: {
              inFlight: true,
              completed: 0,
              total: 0,
              currentIndex: 0,
              current: 'alerts',
              parallelSteps: [],
              verb
            }
          });
        }
        await Promise.resolve(this.props.syncAlertsFromTimelineResults());
        this.lastAlertRefreshAt = now;
      }

      // 5) map markers (Observations + Tracks) — fetch was started early above
      setLoadingStep(5, 'map markers');
      try {
        await markersPromise;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('fetchAllData step failed: markers', error);
      } finally {
        finishStepTiming('markers fetch', markersFetchStartedAt);
      }
      const markersRenderStartedAt = performance.now();
      await waitForNextPaint();
      await waitForNextPaint();
      finishStepTiming('markers render', markersRenderStartedAt);
      completed += 1;

      // 6) agent list
      const agentListStartedAt = performance.now();
      setLoadingStep(6, 'agent list');
      let agentListResponse = null;
      try {
        agentListResponse = await this.awaitWithTimeout(
          Promise.resolve(this.props.getAgents()),
          25000,
          'agent list'
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('fetchAllData step failed: agents', error);
      } finally {
        finishStepTiming('agent list', agentListStartedAt);
      }
      completed += 1;

      // 7) agent markers
      const agentsFetchOk =
        agentListResponse &&
        !agentListResponse.timedOut &&
        !agentListResponse.isError;
      if (agentsFetchOk) {
        const agentMarkersStartedAt = performance.now();
        setLoadingStep(7, 'agent markers');
        try {
          await this.awaitWithTimeout(
            this.props.getAllLayerFeatures({
              onlyCommon: true,
              commonLayerNames: ['Agents']
            }),
            30000,
            'agent markers'
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('fetchAllData step failed: agent markers', error);
        } finally {
          finishStepTiming('agent markers', agentMarkersStartedAt);
        }
        completed += 1;
      }
      // eslint-disable-next-line no-console
      console.info(
        '[fetchAllData] timings summary:',
        trackedStepOrder.reduce((acc, key) => {
          if (Number.isFinite(stepDurationsMs[key])) {
            acc[key] = stepDurationsMs[key];
          }
          return acc;
        }, {})
      );
      this.lastCompletedQueryFingerprint = queryFingerprint;
    } finally {
      this.fetchAllDataInFlight = false;
      if (startedPipeline) {
        this.hasCompletedInitialDataLoad = true;
      }
      if (!silent) {
        this.setState({
          dataLoading: {
            inFlight: false,
            completed: 0,
            total: 0,
            currentIndex: 0,
            current: '',
            parallelSteps: [],
            verb: this.hasCompletedInitialDataLoad ? 'Updating' : 'Loading'
          }
        });
      }
      if (this.fetchAllDataPending) {
        const nextOptions = this.pendingFetchOptions || {};
        this.fetchAllDataPending = false;
        this.pendingFetchOptions = null;
        this.fetchAllData(nextOptions);
      }
    }
  };

  _handleHistogramHeight(px) {
    const rounded = Math.round(px);
    if (rounded !== this._lastHistogramHeight) {
      this._lastHistogramHeight = rounded;
      this.setState({ histogramHeightPx: rounded });
    }
  }

  getLKPData = async () => {
    await this.props.getAgentLayerFeatures();
  };

  setTransientLoading = (inFlight, current) => {
    this.setState({
      transientLoading: {
        inFlight,
        completed: 0,
        total: 0,
        currentIndex: 0,
        current: inFlight ? current || 'tracks' : '',
        parallelSteps: [],
        verb: this.hasCompletedInitialDataLoad ? 'Updating' : 'Loading'
      }
    });
  };

  resetAllFilters = async () => {
    const todayStart = moment()
      .startOf('day')
      .format('YYYY-MM-DD');
    const tomorrowStart = moment()
      .startOf('day')
      .add(1, 'day')
      .format('YYYY-MM-DD');
    const dynamicLayerNames = Array.isArray(this.props.mapLayerNames)
      ? this.props.mapLayerNames
      : [];
    const selectedLayers = Array.from(
      new Set([
        'Observations',
        'Tracks',
        'Agents',
        ...dynamicLayerNames.filter(name => typeof name === 'string')
      ])
    );

    if (this.fetchAllData && this.fetchAllData.cancel) {
      this.fetchAllData.cancel();
    }
    this.setTransientLoading(false, '');
    // Clear map layers immediately so reset feedback is instant.
    await Promise.allSettled([
      Promise.resolve(this.props.clearSelectedItem()),
      Promise.resolve(this.props.clearEntireTrack()),
      Promise.resolve(this.props.clearCommonLayerFeatures())
    ]);

    await Promise.allSettled([
      Promise.resolve(this.props.setSearchQuery('')),
      Promise.resolve(this.props.setSelectedConcepts([])),
      Promise.resolve(this.props.setMode(true)),
      Promise.resolve(this.props.setSelectedDataSources([])),
      Promise.resolve(this.props.setSelectedAgents([])),
      Promise.resolve(this.props.setStartEnd(todayStart, tomorrowStart)),
      Promise.resolve(this.props.setSelectedLayers(selectedLayers))
    ]);

    this.setState(
      prevState => ({ resetVersion: prevState.resetVersion + 1 }),
      () => {
        this.fetchAllData({ force: true, refreshAlerts: true });
      }
    );
  };

  collapseFilter = async () => {
    await this.setState({
      filterCollapsed: !this.state.filterCollapsed,
      filterPanelWidthPx: null
    });
  };

  expandFilter = async () => {
    await this.setState({
      filterExpanded: !this.state.filterExpanded,
      filterPanelWidthPx: null
    });
  };

  setMobileView = mobileView => {
    setHeaderMenuOpen(false);
    if (this.state.mobileView !== mobileView) {
      this.setState({ mobileView });
    }
  };

  render() {
    const {
      isMobile,
      mobileView,
      dataLoading,
      transientLoading,
      ontologyLoading
    } = this.state;
    const effectiveDataLoading = transientLoading.inFlight
      ? transientLoading
      : dataLoading;
    const currentLoadingStep = String(
      effectiveDataLoading.current || ''
    ).trim();
    const currentParallelSteps = Array.isArray(
      effectiveDataLoading.parallelSteps
    )
      ? effectiveDataLoading.parallelSteps
      : [];
    const selectedProcessingLabel =
      currentParallelSteps.length > 0
        ? String(currentParallelSteps[0] || '').trim()
        : currentLoadingStep;
    const processingStepLabels = {
      'sensing clues data model': 'Sensing Clues data model',
      'data sources': 'data sources',
      'agent list': 'agents',
      'concept counts': 'concept counts',
      results: 'results',
      'observation markers': 'observation markers',
      'track markers': 'track markers',
      'loading tracks...': 'track details...',
      'loading track details...': 'track details...',
      'loading all track traces...': 'all track traces...',
      'loading track traces...': 'track traces...',
      'loading selected track traces...': 'selected track traces...'
    };
    const normalizedProcessingLabel = selectedProcessingLabel.toLowerCase();
    const mappedLabel = Object.prototype.hasOwnProperty.call(
      processingStepLabels,
      normalizedProcessingLabel
    )
      ? processingStepLabels[normalizedProcessingLabel]
      : selectedProcessingLabel;
    const processingStatusText = mappedLabel
      ? /^loading\b/i.test(mappedLabel)
        ? mappedLabel
        : `Loading ${mappedLabel}`
      : '';
    const showTopRequestStatus =
      effectiveDataLoading.inFlight && Boolean(processingStatusText);
    const filterResizeBounds = this.getFilterResizeBounds();
    const resultsResizeBounds = this.getResultsResizeBounds();
    return (
      <div
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        className={isMobile ? 'mobile-home-shell' : ''}
      >
        {/* On mobile: free text search shows in Filters and Results, the period
            controls in Filters only, and the Map view hides both. Hidden rather
            than unmounted, so SearchBar keeps its local state. */}
        <div
          style={
            isMobile && mobileView === 'map' ? { display: 'none' } : undefined
          }
        >
          <SearchBar
            hideMobilePeriod={isMobile && mobileView === 'results'}
            expandPeriodVersion={this.state.expandPeriodVersion}
            setTimePeriod={this.setTimePeriod}
            toggleMapLayer={this.toggleMapLayer}
            fetchAllData={this.fetchAllData}
            showProcessingStatus={showTopRequestStatus}
            processingStatusText={processingStatusText}
            onResetAllFilters={this.resetAllFilters}
            resetVersion={this.state.resetVersion}
          />
        </div>
        <div
          className={
            isMobile
              ? 'row map no-gutters mobile-map-layout'
              : 'row map no-gutters layout-row-desktop'
          }
          style={
            isMobile
              ? undefined
              : { flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }
          }
        >
          <Filter
            filterCollapsed={this.state.filterCollapsed}
            filterExpanded={this.state.filterExpanded}
            fetchAllData={this.fetchAllData}
            ontologyLoading={ontologyLoading}
            dataLoading={effectiveDataLoading}
            onRetryOntologyLoad={() =>
              this.loadFilterTree({ forceRefresh: true, offlineOnly: false })
            }
            setTransientLoading={this.setTransientLoading}
            resetVersion={this.state.resetVersion}
            filterPanelWidthPx={this.state.filterPanelWidthPx}
            onFilterPanelResize={this.setFilterPanelWidth}
            filterResizeMinPx={filterResizeBounds.minWidthPx}
            filterResizeMaxPx={filterResizeBounds.maxWidthPx}
            isMobile={isMobile}
            mobileActive={mobileView === 'filters'}
            onCloseMobilePane={() => this.setMobileView('map')}
          />
          <MapComponent
            filterCollapsed={this.state.filterCollapsed}
            filterExpanded={this.state.filterExpanded}
            handleExpandFilter={this.expandFilter}
            handleCollapseFilter={this.collapseFilter}
            fetchAllData={this.fetchAllData}
            dataLoading={effectiveDataLoading}
            filterPanelWidthPx={this.state.filterPanelWidthPx}
            resultsPanelWidthPx={this.state.resultsPanelWidthPx}
            showProcessingStatus={showTopRequestStatus}
            processingStatusText={processingStatusText}
            getLKPData={this.getLKPData}
            filterTreeFetched={this.state.filterTreeFetched}
            resultsCollapsed={this.state.resultsCollapsed}
            isMobile={isMobile}
            mobileActive={mobileView === 'map'}
          />
          <ObservationComponent
            resultsCollapsed={this.state.resultsCollapsed}
            resultsPanelWidthPx={this.state.resultsPanelWidthPx}
            onResultsPanelResize={this.setResultsPanelWidth}
            onToggleResultsPanel={this.toggleResultsPanel}
            resultsResizeMinPx={resultsResizeBounds.minWidthPx}
            resultsResizeMaxPx={resultsResizeBounds.maxWidthPx}
            fetchAllData={this.fetchAllData}
            dataLoading={effectiveDataLoading}
            setTransientLoading={this.setTransientLoading}
            resetVersion={this.state.resetVersion}
            isMobile={isMobile}
            mobileActive={mobileView === 'results'}
            onCloseMobilePane={() => this.setMobileView('map')}
          />
        </div>
        {!isMobile && (
          <HistogramPanel
            isMobile={isMobile}
            onHeightChange={this._handleHistogramHeight}
          />
        )}
        {isMobile && (
          <div
            className="mobile-bottom-nav"
            role="tablist"
            aria-label="Mobile navigation"
          >
            <button
              type="button"
              className={mobileView === 'filters' ? 'active' : ''}
              onClick={() => this.setMobileView('filters')}
            >
              Filters
            </button>
            <button
              type="button"
              className={mobileView === 'map' ? 'active' : ''}
              onClick={() => this.setMobileView('map')}
            >
              Map
            </button>
            <button
              type="button"
              className={mobileView === 'results' ? 'active' : ''}
              onClick={() => this.setMobileView('results')}
            >
              Results
            </button>
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={() => setHeaderMenuOpen()}
            >
              More
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default Home;
